/// <amd-module name="Wiki/WitQuerySelector/QueriesTree" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IListSelection, ListSelection, renderListCell } from "VSSUI/List";
import { Spinner } from "VSSUI/Spinner";
import { renderLoadingCell, SimpleTableCell } from "VSSUI/Table";
import { ITreeColumn, ITreeRow, Tree, TreeExpand } from "VSSUI/TreeEx";
import { preventDefault } from "VSSUI/Util";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

import { IFavoritesCollection, IFavoritesService } from "Favorites/Common/FavoritesService";
import { QueryBatchGetRequest, QueryErrorPolicy, QueryExpand } from "Wiki/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient, IWorkItemTrackingRestClient } from "Wiki/Clients/Wit/RestClient/WorkItemTracking";

import { getIconName, getQueryProperties, getSharedQueries, pathSeparator, QueryProperties, QueryTreeItem } from "Wiki/WitQuerySelector/Helper";
import * as Resources from "Wiki/WitQuerySelector/Resources";

export interface IQueriesTreeProps {
    projectId: string;
    onQuerySelected: (queryId: string, queryProperties: QueryProperties, insertQuery?: boolean) => void;
    resetQuery: () => void;
    treeItemProvider?: TreeItemProvider<QueryTreeItem>; // used in UTs
}

export interface IQueriesTreeState {
    loadingInProgress?: boolean;
}

const NoQueriesNodeId = "NoQueries_Node";
const NoFavoritesNodeId = "NoFavorites_Node";
const LoadingNodeId = "Loading_Node";
const FavoriteNodeId = "Favorites_rootNode";

const noQueriesNode = {
    id: NoQueriesNodeId,
    name: Resources.NoQueriesNodeTitle,
    path: Resources.NoQueriesNodeTitle
} as QueryTreeItem;
const favoriteNode = {
    id: FavoriteNodeId,
    name: Resources.FavoritesNodeTitle,
    isFolder: true,
    path: Resources.FavoritesNodeTitle
} as QueryTreeItem;
const noFavoritesNode = {
    id: NoFavoritesNodeId,
    name: Resources.NoFavoritesNodeTitle,
    path: Resources.NoFavoritesNodeTitle
} as QueryTreeItem;
const loadingNode = {
    id: LoadingNodeId
} as QueryTreeItem;

export class QueriesTree extends VssComponent<IQueriesTreeProps, IQueriesTreeState> {
    private _witRestClient: IWorkItemTrackingRestClient;
    private _treeItemsProvider: TreeItemProvider<QueryTreeItem>;
    private _treeColumns: ITreeColumn<QueryTreeItem>[];
    private _queryChildrenLoadedMap: { [key: string]: boolean };
    private _selection: IListSelection;

    constructor(props: IQueriesTreeProps, context: IVssComponentContext) {
        super(props, context);

        this._witRestClient = getWorkItemTrackingClient(context.pageContext);
        this._treeItemsProvider = props.treeItemProvider || new TreeItemProvider<QueryTreeItem>([]);
        this._treeColumns = this._getTreeColumns();
        this._queryChildrenLoadedMap = {};
        this._selection = new ListSelection(false);

        this.state = { loadingInProgress: true };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._fetchInitialQueries();
    }

    public render(): JSX.Element | null {
        const { loadingInProgress } = this.state;

        if (loadingInProgress) {
            return <Spinner label={Resources.LoadingQueriesSpinnerLabel} />;
        }

        return (
            <Tree<QueryTreeItem>
                columns={this._treeColumns}
                itemProvider={this._treeItemsProvider}
                onToggle={this._onToggle}
                selection={this._selection}
                scrollable={true}
                onSelect={this._onTreeRowSelected}
                onActivate={this._onTreeRowActivated}
                singleClickActivation={false}
            />
        );
    }

    // public for UTs.
    public _fetchInitialQueries(): Promise<void> {
        const sharedQueriesPromise = this._witRestClient.getQueries(this.props.projectId, QueryExpand.Minimal, 1).then(queries => {
            queries = getSharedQueries(queries);
            queries.forEach(query => {
                if (query.isFolder) {
                    query.children = query.hasChildren ? getSharedQueries(query.children) : [noQueriesNode];
                }
            });
            return queries;
        });

        const favoritesService = this.context.pageContext.getService<IFavoritesService>("IFavoritesService");
        const favoritedSharedQueriesPromise = favoritesService
            .getFavorites("Microsoft.TeamFoundation.WorkItemTracking.QueryItem", "Project", this.props.projectId)
            .then((favorites: IFavoritesCollection) => {
                if (favorites.value.length === 0) {
                    // no favorites found.
                    const favoritedQueries = [noFavoritesNode];
                    return favoritedQueries;
                } else {
                    const favoritedIds = favorites.value.filter(favorite => !favorite.artifactIsDeleted).map(favorite => favorite.artifactId);
                    const queryGetRequest: QueryBatchGetRequest = {
                        $expand: QueryExpand.Minimal,
                        errorPolicy: QueryErrorPolicy.Omit,
                        ids: favoritedIds
                    };
                    return this._witRestClient.getQueriesBatch(queryGetRequest, this.props.projectId).then(queries => {
                        let favoritedQueries: QueryTreeItem[] = getSharedQueries(queries).map(query => {
                            return { ...query, nodeId: "Favorite:" + query.id };
                        });

                        if (favoritedQueries.length === 0) {
                            // no shared favorites found
                            favoritedQueries = [noFavoritesNode];
                        }
                        return favoritedQueries;
                    });
                }
            });

        return this.trackPromise(Promise.all([sharedQueriesPromise, favoritedSharedQueriesPromise])).promise.then(
            ([sharedQueries, favoritedSharedQueries]) => {
                const itemsToAdd: ITreeItem<QueryTreeItem>[] = [
                    {
                        data: favoriteNode,
                        expanded: true,
                        childItems: _getTreeItems(favoritedSharedQueries)
                    }
                ];

                this._queryChildrenLoadedMap[favoriteNode.id] = true;

                sharedQueries.forEach(query => {
                    itemsToAdd.push({
                        data: query,
                        expanded: true,
                        childItems: _getTreeItems(query.children)
                    });
                    this._queryChildrenLoadedMap[query.id] = true;
                });

                this._treeItemsProvider.splice(undefined, undefined, [{ items: itemsToAdd }]);
                this.setState({ loadingInProgress: false });
            }
        );
    }

    private _onToggle = (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<QueryTreeItem>): void => {
        const underlyingItem = treeItem.underlyingItem;
        const item = underlyingItem.data;
        if (!this._queryChildrenLoadedMap[item.id]) {
            const loadingCell = _getTreeItems([loadingNode]);
            this._treeItemsProvider.splice(underlyingItem, undefined, [{ items: loadingCell }]);
            this._treeItemsProvider.toggle(underlyingItem);
            this._queryChildrenLoadedMap[item.id] = true;
            this._witRestClient.getQuery(this.props.projectId, item.id, QueryExpand.Minimal, 1).then(queryItem => {
                let queryChildren: QueryTreeItem[] = [];
                if (queryItem.hasChildren) {
                    queryChildren = getSharedQueries(queryItem.children);
                } else {
                    queryChildren.push(noQueriesNode);
                }
                this._treeItemsProvider.splice(underlyingItem, loadingCell, [{ items: _getTreeItems(queryChildren) }]);
            });
        } else {
            this._treeItemsProvider.toggle(underlyingItem);
        }
    };

    private _onSizeTreeColumns = (event: MouseEvent, index: number, width: number): void => {
        (this._treeColumns[index].width as ObservableValue<number>).value = width;
    };

    private _getTreeColumns(): ITreeColumn<QueryTreeItem>[] {
        return [
            {
                id: "tree-name-column",
                name: Resources.NameColumnHeaderText,
                renderCell: (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<QueryTreeItem>, treeItem: ITreeItemEx<QueryTreeItem>) => {
                    return _onRenderTreeColumn(rowIndex, columnIndex, treeColumn, treeItem, this._onToggle);
                },
                hierarchical: true,
                minWidth: 100,
                readonly: true,
                width: new ObservableValue(-100),
                onSize: this._onSizeTreeColumns
            },
            {
                id: "tree-path-column",
                name: Resources.PathColumnHeader,
                renderCell: _onRenderPathColumn,
                minWidth: 250,
                readonly: true,
                width: new ObservableValue(300),
                onSize: this._onSizeTreeColumns
            }
        ];
    }

    private _onTreeRowSelected = (event: React.SyntheticEvent<HTMLElement>, treeRow: ITreeRow<QueryTreeItem>): void => {
        const selectedItem = treeRow.data.underlyingItem.data;
        if (selectedItem.isFolder || _isDummyNode(selectedItem.id)) {
            this.props.resetQuery();
        } else {
            this.props.onQuerySelected(selectedItem.id, getQueryProperties(selectedItem, false), false);
        }
    };

    private _onTreeRowActivated = (event: React.SyntheticEvent<HTMLElement>, treeRow: ITreeRow<QueryTreeItem>): void => {
        const treeItem = treeRow.data.underlyingItem.data;
        if (treeItem && !treeItem.isFolder && !_isDummyNode(treeItem.id)) {
            this.props.onQuerySelected(treeItem.id, getQueryProperties(treeItem, false), true);
        }
    };
}

// export for UTs
export function _onRenderTreeColumn(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<QueryTreeItem>,
    treeItem: ITreeItemEx<QueryTreeItem>,
    onToggle: (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<QueryTreeItem>) => void
): JSX.Element {
    const { underlyingItem } = treeItem;
    const queryItem = underlyingItem.data;
    let treeCellContent: JSX.Element | null = null;
    if (_isDummyNode(queryItem.id)) {
        treeCellContent = (
            <span className="flex-row">
                <span className="query-title secondary-text">{queryItem.name}</span>
            </span>
        );
    } else if (queryItem.id === LoadingNodeId) {
        treeCellContent = renderLoadingCell();
    } else {
        const iconName = getIconName(queryItem.queryType);
        treeCellContent = renderListCell({ iconProps: { iconName }, text: queryItem.name });
    }

    return (
        <SimpleTableCell columnIndex={columnIndex} className={"bolt-tree-cell"} key={"query-col-" + rowIndex} tableColumn={treeColumn}>
            <TreeExpand
                depth={treeItem.depth}
                expanded={treeItem.underlyingItem.expanded}
                onClick={preventDefault}
                onToggle={
                    queryItem.isFolder
                        ? (event: React.SyntheticEvent<HTMLElement>) => {
                              onToggle(event, treeItem);
                          }
                        : undefined
                }
            >
                {treeCellContent}
            </TreeExpand>
        </SimpleTableCell>
    );
}

// export for UTs
export function _onRenderPathColumn(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<QueryTreeItem>,
    treeItem: ITreeItemEx<QueryTreeItem>
): JSX.Element {
    const { underlyingItem } = treeItem;
    const queryItem = underlyingItem.data;
    let cellContent: JSX.Element | null = null;
    if (queryItem.nodeId) {
        // in case of favorites
        const path = queryItem.path;
        const index = path.lastIndexOf(pathSeparator); // removing the file name to get just the folder path
        cellContent = renderListCell(path.substring(0, index));
    }

    return (
        <SimpleTableCell columnIndex={columnIndex} className={"bolt-tree-cell"} key={"query-path-col-" + rowIndex} tableColumn={treeColumn}>
            {cellContent}
        </SimpleTableCell>
    );
}

function _isDummyNode(id: string): boolean {
    return id === NoQueriesNodeId || id === NoFavoritesNodeId;
}

function _getTreeItems(items: QueryTreeItem[]): ITreeItem<QueryTreeItem>[] {
    return items.map(item => {
        return {
            data: item,
            childItems: item.isFolder ? [] : undefined
        };
    });
}
